"use client";
import React, { useRef } from "react";
import { ProgramDetailContentProps } from "@/components/ProgramDetailLayout/types";
import InstructorList from "@/components/CourseContent/ContentRefresh/InstructorList";
import ProgramContent from "@/components/CourseContent/ContentOverview";
import WhatYoullLearn from "@/components/CourseContent/ContentRefresh/WhatYoullLearn";
import {
  fullWidthContainer,
  mainContent as mainContentClass,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import ProgramPromoBanner from "../../ProgramPromoBanner";
import { getAllInstructorsFromCourse } from "@/lib/instructor/getAllInstructorsFromCourse";
import getProgramDuration from "@/lib/program/utils/getProgramDuration";
import getProgramFromSubject from "@/components/ProgramDetailLayout/templates/utils/getSubjectFromProgram";
import ProgramRelatedContent from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramRelatedContent";
import { CourseVideo } from "@/components/CourseContent/ContentOverview/types";
import IndustryInsights from "@/components/ProgramDetailLayout/templates/NewDesign/components/IndustryInsights";
import checkRTL from "@/utils/functions/checkRTL";
import sortBySortValue from "@/utils/functions/sortBySortValue";
import ProgramSummaryPathway from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramSummaryPathway";
import { SITE_URL } from "@/lib/program/utils/seo/generateProgramSeo";
import SocialShare from "@/components/SocialShare";
import ProgramEndorsements from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramEndorsements";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import ProgramEnrollButton from "@/components/ProgramDetailLayout/templates/components/ProgramEnrollButton";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import TaggedHeader from "@/components/ProgramDetailLayout/templates/components/TaggedHeader";
import MidPageCTABox from "@/components/ProgramDetailLayout/templates/components/MidPageCTABox";
import ProgramStickyCTA from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramStickyCTA";
import Highlights from "@/components/Highlights";
import ProgramAccordion from "@/components/ProgramDetailLayout/templates/ValueOriented/components/ProgramAccordion";
import AlumniTestimonials from "@/components/AlumniTestimonials";
import PartnershipDescription from "@/components/ProgramDetailLayout/templates/components/PartnershipDescription";
import { PartialPartner } from "@/common/partner/types";
import ProgramFAQs from "../NewDesign/components/ProgramFAQs";
import EnterpriseCTA from "@/components/CourseContent/ContentRefresh/EnterpriseCTA";
import TechMicrocredentialsCTA from "../components/TechMicrocredentialsCTA";
import AccordionTextItem from "@/components/AccordionText/AccordionTextItem";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";
import AccordionText from "@/components/AccordionText";
import Stats from "@/components/Stats";
import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import { getProgramSearchUrl } from "@/components/CourseContent/templates/common/utils/getProgramSearchUrl";
import CareerCards from "@/components/CareerCards";
import {
  CAREER_CARDS_VARIANTS,
  resolveCareerCardSkills,
  getIndustryKey,
} from "@/components/CareerCards/constants";
import RelatedProgram from "@/components/CourseContent/templates/common/utils/renderRelatedProgram";
import { programDetailPageEventConfig } from "../../constants";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import ProductCompareWithPartnerPrograms from "@/components/ProductCompare/components/ProductCompareWithPartnerPrograms";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useEmailOptIn from "../context/ProgramDetailContext/hooks/useEmailOptIn";
import useEnrollmentUrl from "@/lib/program/utils/programEnroll/useEnrollmentUrl";
import { useProgramCompareCtaUrl } from "@/components/ProductCompare/useProgramCompareCtaUrl";
import useCertificateCTAText from "@/monarch/rules/certificateCTA/useCertificateCTAText";
import { useDetailPricingData } from "@/lib/checkout-geo-pricing";
import { ProgramCurrencyBarSurface } from "../components/ProgramCurrencySurfaces";
import useTranslations from "@/i18n/useTranslations";

export default function ResearcherProgramContent({
  programData,
  courses,
  pricingData: serverPricingData,
  skus,
  programIsEnrollable,
  programIsRetired = false,
  upsaleTechMicrocredsCourses,
  enrichedInstructors,
}: ProgramDetailContentProps) {
  const tProgram = useTranslations("Program");
  const tExpand = useTranslations("ExpandableBlock");
  const {
    geoPricing,
    headerCta,
    promoBanner,
    mainContent,
    whyTakeProgram,
    highlights,
    alumniTestimonials,
    partnershipDescription,
    statsBanner,
    bundleSavingsBanner,
    benefitsModule,
    showDynamicBadges,
    careerCards,
    testimonialsModule,
    expandableRelatedPrograms,
    productCompare,
  } = useProgramRedesignConfig();

  const pricingData = useDetailPricingData({
    pricingData: serverPricingData,
    geoPricing,
    product: {
      productType: programData.productType,
      partners: programData.partners?.map((partner) => ({ key: partner.key })),
    },
  });

  const { expectedLearningItems } = programData;

  const { enrollments } = useClientEnrollments();
  const { entitlements } = useClientEntitlements();
  const { emailOptIn } = useEmailOptIn();

  const enrollmentUrl = useEnrollmentUrl(
    programData,
    courses,
    skus,
    enrollments,
    entitlements,
    emailOptIn,
  );
  const programCompareCtaUrl = useProgramCompareCtaUrl({
    programData,
    courses,
    skus,
    programIsEnrollable,
  });

  const { ctaText: enrollCtaLabel } = useCertificateCTAText({
    isProgramEnrollable: true,
  });

  const programOverviewVideo =
    programData.video.length > 0
      ? (programData.video[0] as CourseVideo)
      : undefined;
  if (programOverviewVideo) {
    // Program videos do not have a thumbnail image, this one was provided as fallback by the design team
    programOverviewVideo.image = {
      src: "/bg-images/video-player-thumbnail.jpg",
    };
  }
  const programSubject = getProgramFromSubject(programData);
  programData.video.length > 0
    ? (programData.video[0] as CourseVideo)
    : undefined;
  const instructors = getAllInstructorsFromCourse(courses);

  const formattedInsights = programData.jobOutlookItems
    ?.filter((item) => item && item.value !== null)
    .map((item) => ({
      value: item.value ?? "",
    }));

  const duration = courses && getProgramDuration(courses);
  const filteredExpectedLearningItems = sortBySortValue(
    expectedLearningItems.filter((item) => item.value),
  );
  const areLearningItemsRTL = checkRTL(
    filteredExpectedLearningItems.map((item) => item.value).join(""),
  );

  const componentList = [
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner &&
          programIsEnrollable &&
          programData.programEligibleForOneClickPurchase ? (
          <ProgramPromoBanner
            courses={courses}
            programData={programData}
            skus={skus}
            data={promoBanner}
          />
        ) : null;
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={
              programData.partners?.length ? programData.partners[0].name : ""
            }
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={programDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
          />
        ) : null;
      },
    },

    {
      key: "whatYoullLearn",
      renderer: () => {
        return filteredExpectedLearningItems &&
          filteredExpectedLearningItems.length > 0 ? (
          <div className="hidden md:block">
            <WhatYoullLearn
              title={tProgram("whatYoullLearn")}
              learningItems={filteredExpectedLearningItems}
              areLearningItemsRTL={areLearningItemsRTL}
            />
          </div>
        ) : null;
      },
    },
    {
      key: "programOverview",
      renderer: () => {
        return (
          <ProgramContent
            key="programOverview"
            title={tProgram("programOverview")}
            fullDescription={programData.overview}
            video={programOverviewVideo}
          />
        );
      },
    },
    {
      key: "programSummaryPathway",
      renderer: () => {
        return (
          <>
            <ProgramSummaryPathway
              key="programSummaryPathway"
              courses={courses}
            />
            {filteredExpectedLearningItems &&
            filteredExpectedLearningItems.length > 0 ? (
              <div className="block md:hidden">
                <AccordionText variant="value-oriented-content" title="">
                  <AccordionTextItem
                    variant="value-oriented-content"
                    trigger={tProgram("whatYoullLearn")}
                  >
                    <WhatYoullLearn
                      learningItems={filteredExpectedLearningItems}
                      areLearningItemsRTL={areLearningItemsRTL}
                      oneColumn={true}
                    />
                  </AccordionTextItem>
                </AccordionText>
              </div>
            ) : null}

            <div className="block md:hidden">
              <AccordionText variant="value-oriented-content" title="">
                <AccordionTextItem
                  variant="value-oriented-content"
                  trigger={tProgram("meetTheInstructors")}
                >
                  <InstructorList
                    instructors={instructors}
                    showSingleColumn={true}
                    hideTitle={true}
                    disableExpandCollapse={true}
                  />
                </AccordionTextItem>
              </AccordionText>
            </div>
          </>
        );
      },
    },
    {
      key: "instructorList",
      renderer: () => {
        return (
          <div className="hidden md:block">
            <InstructorList instructors={instructors} />
          </div>
        );
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "statsBanner",
      renderer: () => {
        return statsBanner && statsBanner.items.length > 0 ? (
          <Stats
            stats={statsBanner.items}
            header={statsBanner.header}
            source={statsBanner.source}
            showSliderOnMobile={statsBanner.showSliderOnMobile}
            variant="source"
          />
        ) : null;
      },
    },
    {
      key: "industryInsights",
      renderer: () => {
        return formattedInsights?.length > 0 ? (
          <IndustryInsights
            key="industryInsights"
            insights={formattedInsights}
          />
        ) : null;
      },
    },
    {
      key: "programEndorsements",
      renderer: () => {
        return programData.corporateEndorsements?.length > 0 ||
          programData.individualEndorsements?.length > 0 ? (
          <ProgramEndorsements
            key="programEndorsements"
            corporateEndorsements={programData.corporateEndorsements}
            individualEndorsements={programData.individualEndorsements}
          />
        ) : null;
      },
    },
    {
      key: "creditPathways",
      renderer: () => {
        return programData.creditRedemptionOverview ? (
          <ProgramContent
            key="creditPathways"
            title={tProgram("certificateCreditPathways")}
            fullDescription={programData.creditRedemptionOverview}
          />
        ) : null;
      },
    },
    {
      key: "programRelatedContent",
      renderer: () => {
        return (
          <ProgramRelatedContent
            key="programRelatedContent"
            programSubject={programSubject}
            title={tProgram("relatedTopics")}
          />
        );
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return programData.faq && programData.faq.length > 0 ? (
          <ProgramFAQs key="faqs" faqs={programData.faq} />
        ) : null;
      },
    },
    {
      key: "midPageCTA",
      renderer: () => {
        return programData.programEligibleForOneClickPurchase &&
          programIsEnrollable ? (
          <MidPageCTABox
            key="midPageCTA"
            programData={programData}
            pricingData={pricingData}
            programIsEnrollable={programIsEnrollable}
            courses={courses}
            skus={skus}
            duration={duration}
          />
        ) : null;
      },
    },
    {
      key: "technicalMicrocredentialsCta",
      renderer: () => {
        return upsaleTechMicrocredsCourses ? (
          <TechMicrocredentialsCTA items={upsaleTechMicrocredsCourses} />
        ) : null;
      },
    },
    {
      key: "alumniTestimonials",
      renderer: () => {
        return (
          <AlumniTestimonials
            key="alumniTestimonials"
            monarchTestimonials={alumniTestimonials}
            programTestimonials={programData.individualEndorsements}
            titleClassName="text-center"
          />
        );
      },
    },
    {
      key: "partnershipDescription",
      renderer: () => {
        return programData.partners?.length && partnershipDescription ? (
          <PartnershipDescription
            key="partnershipDescription"
            title={partnershipDescription.title}
            prePartnerText={partnershipDescription.prePartnerText}
            postPartnerText={partnershipDescription.postPartnerText}
            partnerLogoList={programData.partners?.map(
              (partner: PartialPartner) => partner.logoUrl,
            )}
            partnerNameList={programData.partners?.map(
              (partner: PartialPartner) => partner.name,
            )}
          />
        ) : null;
      },
    },
    {
      key: "highlights",
      renderer: () => {
        return highlights ? (
          <Highlights
            key="highlights"
            className="bg-putty-300"
            title={highlights.title}
            items={highlights.items}
            partnerName={
              programData.partners?.length ? programData.partners[0].name : ""
            }
            isEnrollable={
              programIsEnrollable &&
              programData.programEligibleForOneClickPurchase
            }
            showSkillsList={highlights.showSkillsList}
            associatedSkills={programData.associatedSkills}
            theme="dark"
            {...(highlights.promo && {
              promo: {
                headline: highlights.promo.headline,
                subtext: highlights.promo.subtext,
                infoText: highlights.promo.infoText,
                headlineMobile: highlights.promo.headlineMobile,
                subtextMobile: highlights.promo.subtextMobile,
                ctaElement: (
                  <ProgramEnrollButton
                    courses={courses}
                    programData={programData}
                    skus={skus}
                    buttonClass="w-full md:w-auto"
                    enableTracking={true}
                  />
                ),
              },
            })}
          />
        ) : null;
      },
    },
    {
      key: "socialShare",
      renderer: () => {
        return (
          <SocialShare
            key="socialShare"
            title={tProgram("shareThisProgram")}
            productUrl={`${SITE_URL.replace(/\/$/, "")}/${programData.marketingUrl.replace(/^\//, "")}`}
            productName={programData.title}
          />
        );
      },
    },
    {
      key: "enterpriseCta",
      renderer: () => {
        return (
          <div
            className="bg-putty-300"
            key="enterpriseCta"
            style={{
              maxWidth: "none",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            <EnterpriseCTA productName={programData.title} variant="compact" />
          </div>
        );
      },
    },
    {
      key: "expandableRelatedPrograms",
      renderer: () => {
        if (!courses || courses.length === 0) return null;
        return (
          <RelatedProgram
            designTobeSelected="2"
            ignorePricingData={true}
            ignoreSubtitle={true}
            course={courses[0]}
            expandableSection={{
              title:
                expandableRelatedPrograms?.title ??
                tProgram("continueYourLearningJourney"),
              subtitle: expandableRelatedPrograms?.subtitle,
              reasons: expandableRelatedPrograms?.reasons,
              showMoreLabel:
                expandableRelatedPrograms?.showMoreLabel ??
                tProgram("showMorePrograms"),
              showLessLabel:
                expandableRelatedPrograms?.showLessLabel ?? tExpand("showLess"),
              backgroundColor:
                expandableRelatedPrograms?.backgroundColor ?? "putty",
              descriptionFeaturedProgramsAppend:
                expandableRelatedPrograms?.descriptionFeaturedProgramsAppend,
            }}
            cardType={expandableRelatedPrograms?.cardType ?? "program-card"}
          />
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        const ctaUrl =
          bundleSavingsBanner?.cta?.linkType === "search"
            ? getProgramSearchUrl(programData.partners)
            : enrollmentUrl;
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreMorePrograms"),
              url: ctaUrl,
              html_id: "bundling-cta",
              location: "program-detail-page",
              name: "program-bundling-CTA",
            }}
            partnerName={programData.partners?.[0]?.name}
            programData={programData}
            courses={courses}
            isEnrollmentUrl={ctaUrl === enrollmentUrl}
          />
        ) : null;
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const category = getIndustryKey({
          title: programData.title,
          subjectEn: programData.subjectEn || "",
          subjectEs: programData.subjectEs || "",
          overview: programData.overview,
          subjects: programData.subjects,
          skills: programData.skills,
        });
        const careerCardsData =
          CAREER_CARDS_VARIANTS[category || "dataScience"];
        return (
          <CareerCards
            location={careerCards?.location || "detail-page"}
            isSideRail={careerCards?.isSideRail || true}
            industry={careerCards?.industry || category || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              programData.skills,
              category || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        if (!productCompare) return null;
        const partnerNames = programData.partners.map((p) => p.name);
        return (
          <ProductCompareWithPartnerPrograms
            productCompare={productCompare}
            currentUuid={programData.objectID}
            partnerNames={partnerNames}
            product={{
              productUuid: programData.objectID,
              partners: programData.partners,
              subjects: programData.subjects,
              subjectEn: programData.subjectEn,
              subjectEs: programData.subjectEs,
              skills: programData.skills,
            }}
            currentCourseCheckoutUrl={programCompareCtaUrl}
            location="program-detail-page"
            hasStickyOffset={
              programData.programEligibleForOneClickPurchase &&
              programIsEnrollable
                ? "all"
                : undefined
            }
            currentCtaLabel={
              programIsEnrollable &&
              programData.programEligibleForOneClickPurchase
                ? enrollCtaLabel
                : undefined
            }
          />
        );
      },
    },
  ];

  const headerRef = useRef<HTMLDivElement | null>(null);

  const variant = headerCta?.bgColor || "light";

  return (
    <>
      <div ref={headerRef}>
        <TaggedHeader
          courses={courses}
          programData={programData}
          variant={variant}
          pricingData={pricingData}
          skus={skus}
          isProgram={true}
          showDynamicBadges={showDynamicBadges}
          programIsRetired={programIsRetired}
        />
      </div>
      {programData.programEligibleForOneClickPurchase && programIsEnrollable ? (
        <ProgramStickyCTA
          programData={programData}
          courses={courses}
          pricingData={pricingData}
          skus={skus}
          reactTop={0}
        />
      ) : null}

      <ProgramCurrencyBarSurface
        pricingData={pricingData}
        programIsEnrollable={programIsEnrollable}
        programIsRetired={programIsRetired}
      />

      <div
        className="bg-putty-100 flex flex-col gap-12 sm:gap-16 pt-12"
        id="program-details-value-body-container"
      >
        <div className={mainContentClass}>
          {getSortedComponentsBySectionOrder({
            componentList,
            sectionsOrder: mainContent?.map((component) => component.component),
          })}
        </div>
      </div>
      {programData.hasOfacRestrictions && <OFACMessage type="program" />}
    </>
  );
}
